import { initializeApp, getApps } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';

/**
 * Firebase Configuration
 * 
 * This setup mirrors the mobile app Firebase configuration and supports environment variables.
 * All Firebase services (Firestore, Auth, Functions) are initialized here and exported for shared use.
 * 
 * Environment Variables (with fallbacks):
 * - VITE_FIREBASE_API_KEY
 * - VITE_FIREBASE_AUTH_DOMAIN
 * - VITE_FIREBASE_PROJECT_ID
 * - VITE_FIREBASE_STORAGE_BUCKET
 * - VITE_FIREBASE_MESSAGING_SENDER_ID
 * - VITE_FIREBASE_APP_ID
 */
const firebaseConfig = {
  apiKey: 'AIzaSyDummyKeyForDevelopment',
  authDomain: 'gig-genie-app.firebaseapp.com',
  projectId: 'gig-genie-app',
  storageBucket: 'gig-genie-app.firebasestorage.app',
  messagingSenderId: '123456789012',
  appId: '1:123456789012:web:abcdef123456',
};

// Initialize Firebase (only if not already initialized)
// This prevents duplicate initialization errors
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

// Initialize Firestore
// Used by: analyticsLogger, firebaseAdminService, firebaseService, connectionService
export const db = getFirestore(app);

// Initialize Auth
// Required for Firestore rules that check request.auth != null
export const auth = getAuth(app);

// Initialize Functions
// Available for Cloud Functions calls
export const functions = getFunctions(app);

/**
 * Sign in anonymously on startup
 * This satisfies Firestore security rules that require request.auth != null
 * Anonymous auth provides a user ID without requiring email/password
 */
if (typeof window !== 'undefined') {
  // Only run in browser environment
  onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('[AUTH] ✅ Signed in anonymously', { uid: user.uid });
    } else {
      // No user signed in, attempt anonymous sign-in
      signInAnonymously(auth)
        .then((userCredential) => {
          console.log('[AUTH] ✅ Signed in anonymously', { uid: userCredential.user.uid });
        })
        .catch((error) => {
          console.error('[AUTH] ❌ Failed to sign in anonymously:', error);
          console.error('[AUTH] ⚠️ Firestore operations may fail due to permission rules');
        });
    }
  });
}

/**
 * Test Firestore connection by writing and reading a test document
 * Call this from browser console: testWebsiteFirestoreConnection()
 */
export async function testWebsiteFirestoreConnection(): Promise<void> {
  try {
    console.log('[TEST] Starting Firestore connection test...');
    
    if (!db) {
      console.error('[TEST] ❌ Firestore db is not initialized');
      return;
    }
    
    console.log('[TEST] ✅ Firestore db is initialized');
    
    // Import Firestore functions
    const { collection, addDoc, getDocs, query, where, deleteDoc, doc, Timestamp } = await import('firebase/firestore');
    
    // Create test document
    const testDoc = {
      userId: 'test_website_connection',
      screenName: 'TestScreen',
      featureName: 'test_connection',
      timestamp: Timestamp.now(),
      appVersion: '1.0.0',
      platform: 'web',
      testMarker: true, // Mark as test document
    };
    
    console.log('[TEST] Writing test document to analytics/events...');
    const eventsRef = collection(db, 'analytics', 'events');
    const docRef = await addDoc(eventsRef, testDoc);
    console.log('[TEST] ✅ Test document written with ID:', docRef.id);
    
    // Read it back
    console.log('[TEST] Reading test document back...');
    const testQuery = query(eventsRef, where('testMarker', '==', true));
    const snapshot = await getDocs(testQuery);
    
    if (snapshot.empty) {
      console.error('[TEST] ❌ Test document not found after write');
      return;
    }
    
    const readDoc = snapshot.docs[0];
    const readData = readDoc.data();
    console.log('[TEST] ✅ Test document read successfully:', readData);
    
    // Clean up - delete test document
    console.log('[TEST] Cleaning up test document...');
    await deleteDoc(doc(db, 'analytics', 'events', readDoc.id));
    console.log('[TEST] ✅ Test document deleted');
    
    console.log('[TEST] ✅✅✅ Firestore connection test PASSED');
    console.log('[TEST] Summary:');
    console.log('  - Project ID: gig-genie-app');
    console.log('  - Collection path: analytics/events ✅');
    console.log('  - Write: SUCCESS ✅');
    console.log('  - Read: SUCCESS ✅');
    console.log('  - Delete: SUCCESS ✅');
    
  } catch (error: any) {
    console.error('[TEST] ❌❌❌ Firestore connection test FAILED');
    console.error('[TEST] Error:', error);
    console.error('[TEST] Error code:', error.code);
    console.error('[TEST] Error message:', error.message);
    
    if (error.code === 'permission-denied') {
      console.error('[TEST] ⚠️ Permission denied - Check Firestore rules');
      console.error('[TEST] ⚠️ Website may need Firebase Auth initialized');
    } else if (error.code === 'unavailable') {
      console.error('[TEST] ⚠️ Firestore unavailable - Check network/Firebase config');
    } else if (error.message?.includes('projectId')) {
      console.error('[TEST] ⚠️ Firebase config incomplete - Missing credentials');
    }
  }
}

// Make function available globally for console testing
if (typeof window !== 'undefined') {
  (window as any).testWebsiteFirestoreConnection = testWebsiteFirestoreConnection;
}

export default app;

